import React from "react";
import { ApiContext } from "../../common/context/ApiContext";
import { labelForTeacher } from "../custom_sections/TeacherSelector";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SchoolDashboard, CustomSection } from "../../common/api";
import Moment from "moment";
import styled from "styled-components";
import { formatIsoDateAsLocalTimezoneDate } from "./common";
import { GENERIC_ITEM_BORDER_RADIUS } from "../../common/constants";
import {
  DashboardCtaButton,
  DataTable,
  SectionTitle,
  SortArrow,
} from "./DashboardStyles";
import {
  COLOR_BORDER_DEFAULT,
  COLOR_SURFACE_CONTAINER,
  COLOR_TEXT_SECONDARY,
} from "../../common/colors";
import { SPACE_2XL, SPACE_LG, SPACE_SM, SPACE_XL } from "../../common/spacing";

const GroupsCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE_LG}px ${SPACE_XL}px;
  max-width: 600px;
  margin: 0 -${SPACE_LG}px;
  padding: ${SPACE_LG}px;
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  background-color: ${COLOR_SURFACE_CONTAINER};
`;

const GroupsIntro = styled.div`
  flex: 1 1 240px;
`;

const GroupsDescription = styled.p`
  margin: ${SPACE_SM}px 0 0;
  font-size: 13px;
  line-height: 22px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const GroupsTable = styled(DataTable)`
  margin-top: ${SPACE_2XL}px;
  && table {
    width: auto;
    font-size: 13px;
  }
  && th:first-child,
  && td:first-child {
    width: auto;
  }
  && td a {
    color: inherit;
    font-weight: 400;
    text-decoration: none;
  }
  && td a:hover {
    text-decoration: underline;
    text-decoration-color: ${COLOR_BORDER_DEFAULT};
  }
`;

export const AdminToolsTab = ({
  dashboard,
}: {
  dashboard: SchoolDashboard & { is_at_school_level: true };
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const { getCustomSections } = React.useContext(ApiContext);
  const [customSections, setCustomSections] = React.useState<
    CustomSection[] | null
  >(null);
  const [isDescendingOrder, setIsDescendingOrder] =
    React.useState<boolean>(true);

  const onClickCreateCustomSection = React.useCallback(
    (schoolId: number) => {
      history.push(`/school/${schoolId}/custom-section/`);
    },
    [history]
  );

  React.useEffect(() => {
    (async () => {
      setCustomSections(await getCustomSections(dashboard.school_id));
    })();
  }, [setCustomSections, dashboard, getCustomSections]);

  const sortCustomSections = React.useCallback(
    (sectionsToSort: CustomSection[], isDescending: boolean) => {
      if (isDescending) {
        setCustomSections(
          sectionsToSort.sort(
            (a, b) =>
              new Date(b.updated_date).getTime() -
              new Date(a.updated_date).getTime()
          )
        );
      } else {
        setCustomSections(
          sectionsToSort.sort(
            (a, b) =>
              new Date(a.updated_date).getTime() -
              new Date(b.updated_date).getTime()
          )
        );
      }
      setIsDescendingOrder(isDescending);
    },
    [setCustomSections, setIsDescendingOrder]
  );

  const formatLastModified = (isoDate: string) => {
    const date = Moment(isoDate);
    if (date.isSame(Moment(), "day")) {
      return t("dashboard_today");
    }
    if (date.isSame(Moment().subtract(1, "day"), "day")) {
      return t("dashboard_yesterday");
    }
    return formatIsoDateAsLocalTimezoneDate(isoDate);
  };

  return (
    <div>
      <GroupsCard>
        <GroupsIntro>
          <SectionTitle>
            {t("dashboard_admin_tools_instructional_groups_title")}
          </SectionTitle>
          <GroupsDescription>
            {t("dashboard_admin_tools_instructional_groups_subtitle")}
          </GroupsDescription>
        </GroupsIntro>
        <DashboardCtaButton
          type="button"
          onClick={() => onClickCreateCustomSection(dashboard.school_id)}
        >
          {t("dashboard_admin_tools_instructional_groups_creation_button")}
        </DashboardCtaButton>
      </GroupsCard>
      <GroupsTable>
        <thead>
          <tr>
            <th scope="col">{t("dashboard_admin_tools_group_name")}</th>
            <th scope="col">{t("dashboard_admin_tools_teachers")}</th>
            <th scope="col">{t("dashboard_admin_tools_number_of_students")}</th>
            <th scope="col" className="text-start">
              {t("dashboard_admin_tools_created_by")}
            </th>
            <th
              scope="col"
              style={{ cursor: "pointer" }}
              onClick={() =>
                customSections &&
                sortCustomSections(customSections, !isDescendingOrder)
              }
            >
              {t("dashboard_admin_tools_last_modified")}
              <SortArrow dir={isDescendingOrder ? "desc" : "asc"} />
            </th>
          </tr>
        </thead>
        <tbody>
          {customSections?.map((section) => (
            <tr key={section.id}>
              <td>
                <Link
                  to={`/school/${dashboard.school_id}/custom-section/${section.id}`}
                >
                  {section.name}
                </Link>
              </td>
              <td>{section.teachers?.length}</td>
              <td>{section.students?.length}</td>
              <td className="text-start">
                {labelForTeacher(section.created_by)}
              </td>
              <td>{formatLastModified(section.updated_date.toString())}</td>
            </tr>
          ))}
        </tbody>
      </GroupsTable>
    </div>
  );
};
